import * as React from 'react';
import { useDispatch } from 'react-redux';
import {
  CardHcd,
  ConsultationReasonHcd,
} from '@maiia/model/generated/model/api-patient/api-patient';
import { FieldProps } from 'formik';
import { Box } from '@mui/material';
import { centersActions, practitionersActions } from '../../../src/actions';
import ProgressiveSelect from './ProgressiveSelect';
import Text from '../../atoms/Text/Text';
import { useTranslation } from '@/src/i18n';
import Loader from '../../atoms/Loader/Loader';
import { analyticsEvent } from '@/src/analytic_tag_manager';
import useConsultationReasons from '@/src/hooks/useConsultationReasons';

type Props = {
  card: CardHcd;
  onReset?: () => void;
} & FieldProps;

const GROUP_NONE = 'Autre(s) motif(s)';

const ConsultationReasonSelect = ({ field, form, card, ...props }: Props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  // Because native select just support string for value, we empty string for placeholder
  const [selectedGroup, setSelectedGroup] = React.useState<string>('');
  const centerId = card.center?.id;
  const { setFieldValue, values } = form;
  const { specialityId, practitionerId } = values;

  const { data, isLoading } = useConsultationReasons({
    centerId,
    specialityId,
    practitionerId: card.type === 'CENTER' ? undefined : practitionerId,
  });

  const reasons = React.useMemo(
    () =>
      (data ?? []).map(item => ({
        ...item,
        groupName: item.groupName ?? GROUP_NONE,
      })),
    [data],
  );

  const sortedReasons = React.useMemo(
    () =>
      reasons.sort(
        (first, second) => (first.position ?? 0) - (second.position ?? 0),
      ),
    [reasons],
  );
  const filteredReasonsBySelectedGroup = React.useMemo(
    () =>
      selectedGroup
        ? sortedReasons.filter(reason => reason.groupName === selectedGroup)
        : [],
    [sortedReasons, selectedGroup],
  );
  const groups = React.useMemo(() => {
    const allGroups = sortedReasons.map(reason => reason.groupName);
    const uniqueGroups = new Set(allGroups);
    // If uniqueGroups has "GROUP_NONE", move it to the end
    if (uniqueGroups.has(GROUP_NONE) && uniqueGroups.size > 1) {
      uniqueGroups.delete(GROUP_NONE);
      uniqueGroups.add(GROUP_NONE);
    }

    return Array.from(uniqueGroups, group => ({
      id: group as string,
      name: group as string,
    }));
  }, [sortedReasons]);

  const onConsultationReasonChange = React.useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const consultationReasonName = e.target.value;
      dispatch(centersActions.reset());
      dispatch(practitionersActions.getActions().reset());
      setFieldValue(field.name, consultationReasonName);
    },
    [],
  );

  React.useEffect(() => {
    // Reset selectedGroup when change speciality
    setSelectedGroup('');
  }, [specialityId]);

  React.useEffect(() => {
    if (groups.length === 1) {
      setSelectedGroup(groups[0].id);
    }
  }, [groups]);

  return isLoading ? (
    <Box textAlign="center">
      <Loader size={20} color="secondary" />
    </Box>
  ) : (
    <>
      {reasons.length === 0 && card.type === 'CENTER' && (
        <Text>{t('parent_center_no_consultation_reason')}</Text>
      )}
      {groups.length > 1 && (
        <select
          data-cy="select-group-reason"
          aria-label={t('consultation_group')}
          value={selectedGroup}
          onChange={async event => {
            analyticsEvent({
              category: 'Booking',
              action: 'SelectGroupConsultationReason',
            });
            setSelectedGroup(event.target.value);
            await setFieldValue(field.name, '');
            if (props?.onReset) props.onReset();
          }}
        >
          <option value="" disabled>
            {t('consultation_group')}
          </option>
          {groups.map(option => (
            <option data-cy={option.name} key={option.id} value={option.name}>
              {option.name}
            </option>
          ))}
        </select>
      )}
      {selectedGroup && (filteredReasonsBySelectedGroup?.length ?? 0) > 0 && (
        <ProgressiveSelect
          isCustom
          data-cy="select-consultation-reason"
          id="select_consultation_reason"
          options={filteredReasonsBySelectedGroup}
          defaultLabel={t('form_rdv_consultation_reason_label')}
          valueProperty="name"
          field={field}
          onChange={onConsultationReasonChange}
          showType
          {...props}
          form={form}
          autoSelectIfOnlyOneOption
          autoSelectCheck={option => option.isAvailable}
          optionRender={(reason: ConsultationReasonHcd) => reason.name || ''}
        />
      )}
    </>
  );
};

export default ConsultationReasonSelect;
